import classNames from 'classnames'
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'
import { type Theme } from '@opallabs/gem'

type ThemeContextValue = Theme
const DEFAULT_THEME_VALUE: Theme = 'light'
const ThemeContext = createContext<ThemeContextValue>(DEFAULT_THEME_VALUE)
const SYSTEM_THEME_QUERY = window.matchMedia('(prefers-color-scheme: dark)')

/**
 * Get the `Theme` that matches the user’s system.
 *
 * @see https://gist.github.com/kulak-at/62a7a5ba4d02142391f39830827bd455
 */
const useSystemTheme = (): Theme => {
  const [isDarkTheme, setIsDarkTheme] = useState(
    () => SYSTEM_THEME_QUERY.matches
  )

  useEffect(() => {
    const listener = (event: MediaQueryListEvent) =>
      setIsDarkTheme(event.matches)
    SYSTEM_THEME_QUERY.addEventListener('change', listener)
    return () => SYSTEM_THEME_QUERY.removeEventListener('change', listener)
  }, [])

  return isDarkTheme ? 'dark' : 'light'
}

/**
 * Considers the `propTheme` and `systemTheme` together to pick the appropriate value.
 *
 * @note - The explicit `"auto"` behavior is intended as a transitional implementation,
 * with the goal of removing the default value for the `theme` prop; whenever
 * that happens, `adaptedPropTheme` can be removed.
 */
const useCalculatedTheme = (propTheme: Theme | 'auto') => {
  const systemTheme = useSystemTheme()
  const adaptedPropTheme = propTheme == 'auto' ? null : propTheme
  return adaptedPropTheme ?? systemTheme
}

/**
 * Wrap component trees in `<ThemeProvider>` to set up the `useTheme` hook for
 * all the components within the tree.
 *
 * @note If you use `<ThemeProvider>` with the `theme` prop set to `"auto"` it
 *   will automatically use the user’s system theme, and automatically switch
 *   when the system theme changes.
 */
export const ThemeProvider = React.forwardRef<
  HTMLDivElement,
  {
    className?: string
    children: ReactNode
    theme?: Theme | 'auto'
  }
>(({ children, className, theme: propTheme = DEFAULT_THEME_VALUE }, ref) => {
  const theme = useCalculatedTheme(propTheme)
  return (
    <ThemeContext.Provider value={theme}>
      <GemThemeContainer className={className} theme={theme} ref={ref}>
        {children}
      </GemThemeContainer>
    </ThemeContext.Provider>
  )
})
ThemeProvider.displayName = 'ThemeProvider'

/**
 * Similar to `<ThemeProvider>`, `<InverseThemeProvider>` sets up the `useTheme` hook for
 * all the components within the tree. `<InverseThemeProvider>` additionally flips the current
 * theme from `light` to `dark` or vice versa. This is useful for components that are designed to
 * contrast against the primary theme of a page, such as Lapidary's Tooltip.
 */
export const InverseThemeProvider = React.forwardRef<
  HTMLDivElement,
  {
    className?: string
    children: ReactNode
    theme?: Theme | 'auto'
  }
>(({ children, className, theme: propTheme = DEFAULT_THEME_VALUE }, ref) => {
  const inverseTheme =
    useCalculatedTheme(propTheme) === 'light' ? 'dark' : 'light'
  return (
    <ThemeContext.Provider value={inverseTheme}>
      <GemThemeContainer className={className} theme={inverseTheme} ref={ref}>
        {children}
      </GemThemeContainer>
    </ThemeContext.Provider>
  )
})
InverseThemeProvider.displayName = 'InverseThemeProvider'

/**
 * Get the theme from the `<ThemeProvider>` at the root of this component’s
 * tree. If this component isn’t in a tree that has `<ThemeProvider>` at the
 * root it will use a default value.
 *
 * @note `useTheme` should not be used to switch between light and dark color theme variables.
 * If at all possible use the appropriate themed CSS Property for the color.
 * @see - `GemThemeContainer` and `@opallabs/gem - GlobalGemColors`
 *
 * @note The default value for trees that don’t have a `<ThemeProvider>` at the
 *   root is not documented and can change at any time. Make sure trees always
 *   have a `<ThemeProvider>`.
 */
export const useTheme = () => useContext(ThemeContext)

const colorContainerStyles = { blockSize: '100%', inlineSize: '100%' }

/**
 * Provides themed custom CSS Properties for the Gem color system.
 * Leverages global emotion styles provided by @opallabs/gem > GlobalGemColors
 */
const GemThemeContainer = React.forwardRef<
  HTMLDivElement,
  {
    className?: string
    children: ReactNode
    theme: Theme
  }
>(({ children, className, theme }, ref) => {
  return (
    <div
      className={classNames('gem-theme-container', className)}
      css={colorContainerStyles}
      data-gem-theme={theme}
      ref={ref}
    >
      {children}
    </div>
  )
})
GemThemeContainer.displayName = 'GemThemeContainer'
